import * as React from 'react';
import { Component } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import NavBar from './components/NavBar';
import * as Calculator from './Calculator.js';
import MeasureUnits from './MeasureUnits.js';
import RunningPace from './RunningPace.js';
import RunningTime from './RunningTime.js';
import Result from './Result.js';
import Distance from './Distance.js';
import PercentAdjuster from './PercentAdjuster.js';
import SneakerListing from './SneakerListing.js';

const FAQ_DATA = [
  {
    question: 'Do carbon-plated running shoes really make you 4% faster?',
    answer:
      'Research published in Sports Medicine found that runners wearing Nike Vaporfly shoes improved their metabolic economy by approximately 4% on average. However, the actual improvement varies by individual—ranging from about 1% to 6%. Factors like your running speed, biomechanics, and training level all influence how much you benefit.',
  },
  {
    question: 'How do I calculate my running pace?',
    answer:
      'Divide your total race time (in seconds) by the distance in miles or kilometers. For example, a 4-hour marathon (14,400 seconds) over 26.219 miles equals about 549 seconds per mile, or 9:09 per mile. Use the free pace calculator above to instantly see your pace and how it would change with a carbon-plated shoe.',
  },
  {
    question: 'What is the difference between the Nike Alphafly and Vaporfly?',
    answer:
      "The Nike Alphafly features more foam cushioning (40mm forefoot stack), two Air Zoom pods, and a full carbon plate—making it more cushioned but slightly heavier. The Vaporfly has a lower stack height, no Air Zoom pods, and is lighter and more responsive. The Alphafly suits runners who prioritize cushioning for longer races, while the Vaporfly is favored for its lightweight feel across all race distances.",
  },
  {
    question: 'Are carbon plate running shoes worth it for beginners?',
    answer:
      "Carbon plate shoes are engineered for race day, not daily training. For beginners, the performance benefit is most noticeable at faster paces (typically under 7:00 per mile). That said, the extra cushioning in modern super shoes can make long distances more comfortable for any ability level. If budget is a concern, reserve them for your target race to maximize both performance and shoe lifespan.",
  },
  {
    question: 'How long do carbon-plated racing shoes last?',
    answer:
      "Most carbon plate racing shoes are built for racing, not daily training, and typically last 200–400 miles depending on the model and runner weight. The premium foam compounds (Nike's ZoomX, Adidas's LightStrike Pro) degrade faster than traditional EVA foams. Many competitive runners save their super shoes exclusively for races and key speed workouts to preserve their performance characteristics.",
  },
];

const ARTICLE_LINKS = [
  {
    title: 'Best Marathon Racing Shoes 2025',
    description: 'In-depth reviews of the top carbon plate shoes for every runner type and budget.',
    to: '/best-marathon-racing-shoes-2025',
  },
  {
    title: 'How Carbon Plate Shoes Work',
    description: 'The science behind why super shoes make you faster, explained clearly.',
    to: '/carbon-plate-running-shoes-explained',
  },
  {
    title: 'Nike Alphafly vs Vaporfly',
    description: "Head-to-head comparison to help you pick the right Nike racing shoe.",
    to: '/nike-alphafly-vs-vaporfly',
  },
  {
    title: 'Alphafly 3 vs Adios Pro 4',
    description: 'ZoomX vs Energy Rods: which marathon race winner is right for you?',
    to: '/alphafly-3-vs-adios-pro-4',
  },
  {
    title: 'Vaporfly 4 vs Metaspeed Sky Paris',
    description: 'The versatile all-rounder vs the stride-type specialist. Which wins?',
    to: '/vaporfly-4-vs-metaspeed-sky-paris',
  },
  {
    title: 'How Much Faster Do Super Shoes Make You?',
    description: 'Real numbers, the research, and what to realistically expect.',
    to: '/how-much-faster-do-super-shoes-make-you',
  },
];

class SpeedBoostShoes extends Component {
  constructor(props) {
    super(props);

    const params = new URLSearchParams(window.location.search);
    const urlPace = params.get('pace') ? Number(params.get('pace')) : null;
    const urlDistance = params.get('distance') || null;
    const urlUnit = params.get('unit') || null;
    const urlPercent = params.get('percent') ? Number(params.get('percent')) / 100 : null;

    const pace = urlPace || 420;
    const distanceSelected = urlDistance || '26.219';
    const unit = urlUnit || 'mi';
    const percent = urlPercent || 0.04;
    const distance = Calculator.getDistance(unit, distanceSelected);
    const runningTime = Calculator.getRunningTime(distance, pace);

    this.state = {
      timeFormatProvided: 'runningPace',
      distanceSelected,
      measureUnitsSelected: unit,
      runningPace: pace,
      runningTime,
      distance,
      percentImprovement: percent,
      shareCopied: false,
    };
  }

  Copyright = () => (
    <Typography variant="body2" color="text.secondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://www.speedboostshoes.com/">
        www.speedboostshoes.com
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );

  handleDistanceChange = (event) => {
    const { timeFormatProvided, measureUnitsSelected } = this.state;
    const distanceInMiles = event.target.value;
    const distance = Calculator.getDistance(measureUnitsSelected, distanceInMiles);
    if (timeFormatProvided === 'runningTime') {
      this.updateRunningPace(distance);
    }
    this.setState({ distanceSelected: distanceInMiles, distance });
  };

  handleMeasureUnitsChange = (event) => {
    const { timeFormatProvided, distanceSelected } = this.state;
    const measureUnits = event.target.value;
    const distance = Calculator.getDistance(measureUnits, distanceSelected);
    if (timeFormatProvided === 'runningTime') {
      this.updateRunningPace(distance);
    }
    this.setState({ measureUnitsSelected: measureUnits, distance });
  };

  handleTimeFormatChange = (event) => {
    this.setState({ timeFormatProvided: event.target.value });
  };

  handleRunningPaceChange = (time) => {
    const runningTime = Calculator.getRunningTime(this.state.distance, time);
    this.setState({ runningTime, runningPace: time });
  };

  handlePercentChange = (percent) => {
    this.setState({ percentImprovement: percent });
  };

  handleRunningTimeChange = (time) => {
    const runningPace = Calculator.getRunningPace(this.state.distance, time);
    this.setState({ runningPace, runningTime: time });
  };

  updateRunningPace = (distance) => {
    const runningPace = Calculator.getRunningPace(distance, this.state.runningTime);
    this.setState({ runningPace });
  };

  handleShareClick = () => {
    const { runningPace, distanceSelected, measureUnitsSelected, percentImprovement } = this.state;
    const percent = Math.round(percentImprovement * 100);
    const shareUrl = `${window.location.origin}/?pace=${Math.round(runningPace)}&distance=${distanceSelected}&unit=${measureUnitsSelected}&percent=${percent}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.setState({ shareCopied: true });
        setTimeout(() => this.setState({ shareCopied: false }), 2000);
      });
    }
  };

  render() {
    const theme = createTheme();
    const {
      timeFormatProvided,
      measureUnitsSelected,
      runningPace,
      runningTime,
      percentImprovement,
      shareCopied,
    } = this.state;

    return (
      <ThemeProvider theme={theme}>
        <Helmet>
          <title>Running Pace Calculator: See Your Carbon Shoe Time Improvement | SpeedBoostShoes.com</title>
          <meta
            name="description"
            content="Free running pace calculator. See exactly how much faster you could run with carbon-plated super shoes like the Nike Alphafly, Vaporfly, or Adidas Adios Pro. Calculate your 5K, 10K, half marathon, and marathon time improvements."
          />
          <link rel="canonical" href="https://www.speedboostshoes.com/" />
          <meta property="og:title" content="Running Pace Calculator | SpeedBoostShoes.com" />
          <meta property="og:description" content="Free calculator: see how much faster carbon-plated super shoes can make you for 5K through marathon." />
          <meta property="og:url" content="https://www.speedboostshoes.com/" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Running Pace Calculator | SpeedBoostShoes.com" />
          <meta name="twitter:description" content="Free calculator: see how much faster carbon-plated super shoes can make you for 5K through marathon." />
        </Helmet>
        <CssBaseline />
        <NavBar />
        <main>
          {/* Calculator hero */}
          <Box sx={{ bgcolor: 'background.paper', pt: { xs: 3, sm: 8 }, pb: 6 }}>
            <Container maxWidth="sm">
              <Typography
                component="h1"
                variant="h3"
                align="center"
                color="text.primary"
                gutterBottom
              >
                Running Pace Calculator
              </Typography>
              <Typography variant="h6" align="center" color="text.secondary" paragraph>
                Enter your current pace or race time to see how much faster you could run
                with a carbon-plated super shoe. Then check out the top-rated racing shoes below.
              </Typography>
              <Box sx={{ flexGrow: 1, border: '1px dashed grey' }}>
                <Grid container spacing={0.5} rowSpacing={0} sx={{ pb: 2 }}>
                  <Grid container direction="column" alignItems="flex-end" justifyContent="flex-end">
                    <Grid item xs={8} sx={{ height: '5%' }}>
                      <MeasureUnits
                        measureUnitsSelected={measureUnitsSelected}
                        onMeasureUnitsChange={this.handleMeasureUnitsChange}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs container direction="row" sx={{ maxHeight: '10%', minHeight: '60px', ml: 1 }}>
                    <Distance
                      timeFormatProvided={this.state.timeFormatProvided}
                      currentValue={this.state.distanceSelected}
                      onDistanceChange={this.handleDistanceChange}
                      onTimeFormatChange={this.handleTimeFormatChange}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ maxHeight: '10%', minHeight: '60px', ml: 1 }}>
                    {timeFormatProvided === 'runningPace' ? (
                      <RunningPace
                        onRunningPaceChange={this.handleRunningPaceChange}
                        runningPace={runningPace}
                      />
                    ) : (
                      <RunningTime
                        onRunningTimeChange={this.handleRunningTimeChange}
                        runningTime={runningTime}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sx={{ maxHeight: '10%', minHeight: '60px', ml: 1 }}>
                    <PercentAdjuster
                      onPercentChange={this.handlePercentChange}
                      percent={percentImprovement}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ ml: 1 }}>
                    <Result data={this.state} />
                  </Grid>
                  <Grid item xs={12} sx={{ ml: 1, mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={this.handleShareClick}
                    >
                      {shareCopied ? '✓ Link Copied!' : 'Share Your Result'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>

          {/* Shoe listings intro */}
          <Box sx={{ bgcolor: 'background.paper', pt: 4, pb: 2 }}>
            <Container maxWidth="sm">
              <Typography variant="h4" component="h2" align="center" color="text.primary" gutterBottom>
                Top Carbon-Plated Racing Shoes
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary" paragraph>
                These are the shoes behind the super shoe revolution—the same category responsible
                for the 4% improvement shown in clinical research. Reviews are based on current
                models as of early 2025.
              </Typography>
            </Container>
          </Box>

          {/* Shoe listings */}
          <Container sx={{ py: 1 }} maxWidth="md">
            <Grid container spacing={2}>
              <SneakerListing
                title="Nike Alphafly 3"
                img="/imgs/alphafly_next_percent.webp"
                img_alt="Nike Alphafly 3 carbon plate racing shoe"
                img_caption="Image credit: Nike"
                link="https://www.nike.com/t/alphafly-3-mens-road-racing-shoes-5gZBgL"
                description="Nike's flagship marathon racing shoe, the Alphafly 3 is built for runners who want maximum energy return and cushioning on race day. It features Nike's ZoomX foam—the lightest and most responsive foam in their lineup—combined with two forefoot Air Zoom pods that add an extra layer of propulsion. The full-length carbon fiber plate ties it all together, stiffening the shoe through the toe-off phase for a powerful, efficient stride."
                description_1="The Alphafly 3 is noticeably more cushioned than its sibling the Vaporfly, making it particularly well-suited for the marathon distance where foam compression late in the race becomes a real factor. At ~$285, it's the premium option in Nike's lineup."
                description_2="Best for: Runners targeting the half marathon or marathon who want maximum cushioning and energy return. The Alphafly's benefits are most pronounced at paces faster than 7:00 per mile."
              />

              <SneakerListing
                title="Nike Vaporfly 3"
                img="/imgs/vaporfly_next_percent.webp"
                img_alt="Nike Vaporfly 3 carbon plate racing shoe"
                img_caption="Image credit: Nike"
                link="https://www.nike.com/t/vaporfly-4-mens-road-racing-shoes-HK05JWOf"
                description="The shoe that started the super shoe revolution, the Nike Vaporfly 3 remains one of the best racing shoes ever made. It's lighter than the Alphafly, uses the same ZoomX foam, and delivers an incredibly propulsive, snappy feel underfoot. While it lacks the Alphafly's Air Zoom pods and has a lower foam stack, many runners actually prefer its more connected, responsive ride."
                description_1="The Vaporfly 3 is versatile enough for races from 5K to the marathon. At ~$260, it's $25 less than the Alphafly and a great choice if you're racing across multiple distances throughout the season."
                description_2="Best for: Runners of all distances who want a lightweight, highly responsive racing shoe with the full benefit of ZoomX foam and carbon plate technology."
              />

              <SneakerListing
                title="Adidas Adizero Adios Pro 4"
                img="/imgs/adidas_adios_pro_3.webp"
                img_alt="Adidas Adizero Adios Pro 4 carbon plate racing shoe"
                img_caption="Image credit: Adidas"
                link="https://www.adidas.com/us/adizero-adios-pro-4-shoes/JR6369.html"
                description="The Adidas Adizero Adios Pro 4 is the shoe that has claimed multiple World Marathon Major victories. What sets the Adios Pro apart is its unique 'Energy Rods' system—five carbon-infused rods running through the midsole that act like tendons, storing and releasing energy with each stride. Combined with the exceptionally bouncy LightStrike Pro foam, the result is a shoe that feels fast and efficient from mile one to the finish."
                description_1="Adidas has refined the fit and upper with each generation, and the Pro 4 feels secure and comfortable without being restrictive. For mid-pack marathon runners (targeting 2:45–4:00), the Adios Pro 4 is one of the most consistent performers on the market at ~$250."
                description_2="Best for: Runners who prefer a distinctive energy-return sensation that feels different from the Nike ZoomX stack. Strong choice for the marathon and half marathon."
              />

              <SneakerListing
                title="Asics Metaspeed Sky Paris"
                img="/imgs/asics_metaspeed_sky.webp"
                img_alt="Asics Metaspeed Sky Paris carbon plate racing shoe"
                img_caption="Image credit: Asics"
                link="https://www.asics.com/us/en-us/metaspeed-sky-paris/p/ANA_1013A123-750.html"
                description="The Asics Metaspeed Sky Paris was developed for the Paris Olympics and is Asics' best race shoe to date. Unlike most super shoes, Asics deliberately engineers two versions: the Sky (for stride-type runners who lengthen their stride to go faster) and the Edge (for cadence-type runners who increase turnover). The Sky Paris uses FF Turbo+ foam—Asics' most responsive compound—with a full-length carbon plate."
                description_1="The Sky Paris delivers a smooth, rolling transition and exceptional energy return in the forefoot. It's slightly more stable than the Vaporfly with a wider platform that many runners find reassuring over long distances. At ~$250, it's a competitive price for this level of performance."
                description_2="Best for: Runners who increase speed primarily by lengthening their stride. Particularly well-suited for taller runners and those who prefer a slightly more cushioned, stable ride."
              />

              <SneakerListing
                title="New Balance FuelCell SuperComp Pacer"
                img="/imgs/new_balance_FuelCell_SuperComp_Pacer.webp"
                img_alt="New Balance FuelCell SuperComp Pacer carbon plate racing shoe"
                img_caption="Image credit: New Balance"
                link="https://www.newbalance.com/"
                description="The New Balance FuelCell SuperComp Pacer brings carbon plate technology to runners looking for a more accessible price point without sacrificing performance. It uses NB's FuelCell foam—a nitrogen-infused compound that's lightweight and highly responsive—with a full-length carbon fiber plate. The result is a snappy, energetic ride that feels genuinely fast."
                description_1="The SuperComp Pacer has a slightly lower stack height than the Elite v4, making it lighter and better suited to shorter distances like the 5K and 10K, though it handles the half marathon comfortably. The fit is secure with a seamless upper that reduces the chance of blisters on race day."
                description_2="Best for: Runners who want carbon plate performance at a more accessible price, or those racing shorter distances who want a nimble, lower-to-the-ground feel."
              />

              <SneakerListing
                title="Saucony Endorphin Pro 4"
                img="/imgs/saucony_ENDORPHIN_PRO_3.jpeg"
                img_alt="Saucony Endorphin Pro 4 carbon plate racing shoe"
                img_caption="Image credit: Saucony"
                link="https://www.saucony.com/en/endorphin-pro-4/"
                description="The Saucony Endorphin Pro 4 is consistently one of the best-value carbon plate racing shoes available. At around $225, it's typically $25–60 less than its main competitors while delivering genuinely elite-level performance. The Pro 4 uses Saucony's PWRRUN PB foam—a nitrogen-infused supercritical foam that's extremely lightweight—paired with a full-length S-shaped carbon fiber plate."
                description_1="The S-shaped plate design acts as both a propulsion lever in the forefoot and a rocker through the midfoot, helping to speed up stride transitions. The fit is comfortable and slightly roomier in the toe box than the Vaporfly, which runners with wider feet often appreciate."
                description_2="Best for: Runners looking for the best price-to-performance ratio in the carbon plate category. Also a great choice for runners with slightly wider feet who find the Nike options too narrow."
              />
            </Grid>
          </Container>

          {/* Articles section */}
          <Box sx={{ bgcolor: 'grey.50', pt: 5, pb: 5 }}>
            <Container maxWidth="md">
              <Typography variant="h4" component="h2" align="center" gutterBottom>
                Learn More About Carbon-Plate Running Shoes
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {ARTICLE_LINKS.map((article) => (
                  <Grid item xs={12} sm={6} key={article.to}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="h3">
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {article.description}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small" component={RouterLink} to={article.to}>
                          Read Article
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* FAQ section */}
          <Box sx={{ bgcolor: 'background.paper', pt: 5, pb: 6 }}>
            <Container maxWidth="md">
              <Typography variant="h4" component="h2" align="center" gutterBottom>
                Frequently Asked Questions
              </Typography>
              {FAQ_DATA.map((faq, index) => (
                <Accordion key={index} sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" component="h3">
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Container>
          </Box>
        </main>

        {/* Footer */}
        <Box sx={{ bgcolor: 'background.paper', p: 6 }} component="footer">
          <Typography variant="h6" align="center" gutterBottom>
            SpeedBoostShoes.com
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" component="p">
            Imagine your new speed!
          </Typography>
          {this.Copyright()}
        </Box>
      </ThemeProvider>
    );
  }
}

export default SpeedBoostShoes;
